/**
 * The session's answer to the one consent question, and what it permits.
 *
 * Asked once, all-or-nothing. Asking up front and capturing up front are different decisions, and
 * this module keeps them apart: the first step is never captured, an exclusion belonging to the step
 * rather than the answer. Ask `mayCaptureStep`, never the answer alone.
 */
import {
  type AiConsent,
  canRecordAiConsent,
  isConsentComplete,
  readAiConsent,
} from "@/components/AIAutomation/ai-consent";
import { isCaptureForbidden } from "@/components/AIAutomation/capture-exclusions";

/** A closed union so "not asked yet" cannot read as "no". */
export type JourneyConsentState =
  | { kind: "ask" }
  /** Both permissions given. */
  | { kind: "granted"; consent: AiConsent }
  /** Answered without both, or refused. Manual path only, and never re-asked. */
  | { kind: "declined" };

/**
 * Narrow the stored answer to what the session may do.
 *
 * A partial answer is a refusal: capture is offered as one decision, so granting half of
 * it is not a grant. Only an absent record leaves the question open.
 */
export const resolveJourneyConsent = (): JourneyConsentState => {
  const consent = readAiConsent();
  if (!consent) {
    return { kind: "ask" };
  }
  return isConsentComplete(consent)
    ? { kind: "granted", consent }
    : { kind: "declined" };
};

/**
 * Whether the question could be put on this step at all, asked before the answer is known.
 *
 * Exported so the page can withhold its chrome while readiness resolves without re-implementing
 * which step asks -- this module owns that rule, and two copies would drift.
 */
export const isStepThatAsks = (currentStepNum: number): boolean =>
  currentStepNum === 1;

/**
 * Whether the consent question should be put to the new hire right now.
 *
 * Every rule lives here so a call site cannot forget one. A hire resuming later is never asked and
 * capture stays off: silence is not a grant.
 */
export const shouldAskJourneyConsent = (
  isCaptureAvailable: boolean,
  state: JourneyConsentState,
  currentStepNum: number
): boolean =>
  isCaptureAvailable &&
  state.kind === "ask" &&
  isStepThatAsks(currentStepNum) &&
  canRecordAiConsent();

/**
 * Whether a frame may be recorded on this step right now.
 *
 * A step the app cannot name is forbidden, since it may be the identity step. A denylist by
 * decision, not oversight: the design captures every step but identity.
 */
export const mayCaptureStep = (
  state: JourneyConsentState,
  stepId: string | undefined
): boolean =>
  stepId !== undefined &&
  !isCaptureForbidden(stepId) &&
  state.kind === "granted";
